import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Star, BellSimple } from "@phosphor-icons/react";
import { AppHeader } from "../components/AppHeader";
import { PriceChart } from "../components/PriceChart";
import { ChangeIndicator } from "../components/ChangeIndicator";
import { DataStatus } from "../components/DataStatus";
import { AlertFormDialog } from "../components/AlertFormDialog";
import { Disclaimer } from "../components/Disclaimer";
import { Skeleton } from "../components/ui/skeleton";
import { useFavorites } from "../hooks/useFavorites";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { fmtMoney, fmtCompactBase } from "../lib/format";

const RANGES = ["1D", "7D", "30D", "3M", "1Y"];

function Stat({ label, value, testid }) {
  return (
    <div className="border border-border bg-card p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-mono text-base font-semibold mt-1" data-testid={testid}>{value}</div>
    </div>
  );
}

export default function CryptoDetail() {
  const { coinId } = useParams();
  const nav = useNavigate();
  const [range, setRange] = useState("7D");
  const { isFav, toggle } = useFavorites();
  const { user } = useAuth();
  const base = user?.base_currency || "USD";

  const detail = useQuery({
    queryKey: ["crypto", coinId, base],
    queryFn: async () => (await api.get(`/crypto/${coinId}`, { params: { base } })).data,
  });
  const history = useQuery({
    queryKey: ["crypto-hist", coinId, range, base],
    queryFn: async () => (await api.get(`/crypto/${coinId}/history`, { params: { range, base } })).data,
  });

  const c = detail.data?.coin;
  const symbol = c ? c.symbol.toUpperCase() : "";
  const fav = c ? isFav("crypto", symbol, coinId) : false;
  const favPayload = c ? { asset_type: "crypto", symbol, name: c.name, coin_id: coinId } : null;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <button onClick={() => nav(-1)} data-testid="back-btn" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft size={16} /> Back
        </button>

        {detail.isLoading ? <Skeleton className="h-32 rounded-none" /> : c && (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
              <div>
                <div className="flex items-center gap-3">
                  {c.image && <img src={c.image} alt="" className="h-9 w-9 rounded-full" />}
                  <h1 className="font-heading text-4xl font-black tracking-tight" data-testid="detail-symbol">{symbol}</h1>
                  <span className="text-muted-foreground">{c.name}</span>
                </div>
                <div className="mt-4 flex items-baseline gap-3">
                  <span className="font-mono text-3xl font-bold tracking-tight" data-testid="detail-price">{fmtMoney(c.current_price, base)}</span>
                  <ChangeIndicator value={c.price_change_percentage_24h} size="lg" />
                </div>
                <span className="mt-2 block"><DataStatus updatedAt={detail.data.updated_at} stale={detail.data.stale} testid="crypto-detail-status" /></span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggle(favPayload)} data-testid="detail-fav" className="inline-flex items-center gap-2 border border-border bg-card px-4 h-10 hover-lift text-sm">
                  <Star size={16} weight={fav ? "fill" : "regular"} className={fav ? "text-primary" : ""} /> {fav ? "Watching" : "Watch"}
                </button>
                <AlertFormDialog asset={{ asset_type: "crypto", symbol, name: c.name, coin_id: coinId }} base={base}
                  trigger={<button data-testid="detail-alert" className="inline-flex items-center gap-2 border border-border bg-card px-4 h-10 hover-lift text-sm"><BellSimple size={16} /> Alert</button>} />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Stat label="24h High" value={fmtMoney(c.high_24h, base)} testid="stat-high" />
              <Stat label="24h Low" value={fmtMoney(c.low_24h, base)} testid="stat-low" />
              <Stat label="Market Cap" value={fmtCompactBase(c.market_cap, base)} testid="stat-mcap" />
              <Stat label="24h Volume" value={fmtCompactBase(c.total_volume, base)} testid="stat-volume" />
            </div>

            <div className="border border-border bg-card p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground">Price History</h3>
                <div className="inline-flex border border-border" data-testid="range-tabs">
                  {RANGES.map((r) => (
                    <button key={r} onClick={() => setRange(r)} data-testid={`range-${r}`}
                      className={`px-3 py-1.5 text-xs font-mono ${range === r ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>{r}</button>
                  ))}
                </div>
              </div>
              {history.isLoading ? <Skeleton className="h-[320px] rounded-none" /> :
                <PriceChart points={history.data?.points} isPrice height={320} />}
            </div>
          </>
        )}
        <Disclaimer />
      </main>
    </div>
  );
}
